import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { forkJoin, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { LoadingState } from 'core/common-entities/loading-state.enum';
import { SortAccounts, SortDirection } from '../../../pos/quotes/entities/sort.enum';
import { AdvancedSearchRequestVars, AdvancedSearchService } from '../../../pos/services/advanced-search.service';
import { StorageValues } from '../../../shared/entities/storage-values.enum';
import { Account, AccountResponse } from '../entities/account-entity.class';
import { AccountsFilters } from '../entities/accounts-filters';

@Injectable({
  providedIn: 'root'
})

/**
 * Service used to process and hold the data relevant to the Accounts list page.
 * The service is responsible for retrieving the server result based on the filters,
 * including pagination, and making sure the response is usable.
 * The service is responsible for getting the template values and merging them with the end result
 * This ensures that all processing is done in a single place, leaving the component to only handle displaying the information
 */
export class AccountsListService {
  accountList$ = new Subject<Account[]>(); // singleton observable. sends the notices to all subscribers
  totalRecords$ = new Subject<number>();
  isProcessing$ = new Subject<LoadingState>();
  currentPage$ = new Subject<number>();

  pageSize = 15;
  private resultCount = {
    exact: 0,
    partial: 0
  };

  defaultSort = {
    direction: 'asc',
    sortBy: 'name1'
  };

  private todayDate = new Date().toISOString();
  private templateValues: any;
  private templateDataState: any;
  private templateCountriesValues: any;
  private isNewSearch = false;
  accRequest = {
    url: '/api/account/list',
    payload: {
      offset: 0,
      page: 1,
      leads: true,
      myCustomers: true,
      delivery: true,
      accounts: true,
      status: 0,
      date: {
        startDate: this.todayDate,
        endDate: this.todayDate
      },
      nameFilter: {
        name: '',
        filterType: 1,
        displayedSearchValue: '',
        partialSearch: false,
        employeeId: ''
      },
      sort: {
        direction: SortDirection[this.defaultSort.direction],
        sortBy: SortAccounts[this.defaultSort.sortBy]
      },
      advancedFilter: {
        priceMatrix: '',
        city: '',
        stateId: null,
        countryId: null,
        postalCode: ''
      }
    }
  };
  private templateApiRequest = {
    templateValuesUrl: '/api/account/templatevalues',
    stateValuesUrl: '/api/contact/statevalues',
    countryList: '/api/contact/countryvalues'
  };
  private pendingRequests = new Subscription();

  constructor(
    private apiService: ApiService,
    private advancedSearchService: AdvancedSearchService,
  ) { }

  /**
   * Gets the Account List first page
   * @param filterFields the filter form values
   */
  getAccountList(filterFields: Partial<AccountsFilters>, sort?: Sort, shouldCache?: boolean) {
    this.accRequest.payload.page = 1; // reset page on each search
    this.isNewSearch = true;

    if (filterFields) {
      this.mapFilters(filterFields);
      this.resetSort(); // sorting is reset and set afterwards
    }

    if (sort) {
      this.accRequest.payload.sort = {
        sortBy: SortAccounts[sort.active].toString(),
        direction: SortDirection[sort.direction].toString()
      };
      sessionStorage.setItem(StorageValues.CachedSort + StorageValues.AccountsKey, JSON.stringify(sort));
    }

    this.getResultsFirstPage();
    if (shouldCache) {
      sessionStorage.setItem(StorageValues.CachedFilters + StorageValues.AccountsKey, JSON.stringify(filterFields));
    }
  }

  resetSort() {
    sessionStorage.removeItem(StorageValues.CachedSort + StorageValues.AccountsKey);
    this.accRequest.payload.sort = {
      sortBy: SortDirection[this.defaultSort.direction],
      direction: SortAccounts[this.defaultSort.sortBy]
    };
  }

  /**
   * Get Template Values
   * if not set, assign the template values to the response.
   * returns an observable
   * @param callback - callback
   */
  getTemplateValues(callback: (...args: any[]) => any) {
    if (this.templateValues) {
      callback(this.templateValues);
      return;
    }

    this.apiService.get(this.templateApiRequest.templateValuesUrl)
      .subscribe(value => {
        this.templateValues = value;
        callback(this.templateValues);
      });
  }

  /**
   * Get Template Values with US states
   * if not set, assign the template values to the response.
   * returns an observable
   * @param callback - callback
   */
  getTemplateValuesWithStates(): Observable<[any, any, any]> {
    const templateValues$ = this.templateValues
      ? of(this.templateValues)
      : this.apiService.get(this.templateApiRequest.templateValuesUrl);

    const stateValues$ = this.templateDataState
      ? of(this.templateDataState)
      : this.apiService.get(this.templateApiRequest.stateValuesUrl);

    const countryValues$ = this.templateCountriesValues
      ? of(this.templateCountriesValues)
      : this.apiService.get(this.templateApiRequest.countryList);

    return forkJoin([templateValues$, stateValues$, countryValues$])
      .pipe(
        tap(([templateValues, stateTemplateValues, countriesTemplateValues]) => {
          this.templateValues = templateValues;
          this.templateDataState = stateTemplateValues;
          this.templateCountriesValues = countriesTemplateValues;
        })
      );
  }

  /**
   * Updates page
   * @param pageIndex - page index
   */
  updatePage(pageIndex) {
    this.accRequest.payload.page = pageIndex;
    this.accRequest.payload.nameFilter.partialSearch = false;
    this.isNewSearch = false;
    this.getResultsFirstPage();
  }

  /**
   * Getting the results for the first page (with or without search terms)
   * @param filterFields the filter value
   */
  private getResultsFirstPage() {
    this.isProcessing$.next(LoadingState.loading);
    this.retrieveAccounts();
  }

  // after loading the first page of results, we know how many exact and how many partial results we have
  private getResultsForRequest(request) {
    // cancel existing requests (if any), and reinitialize the subscription
    this.pendingRequests.unsubscribe();
    this.pendingRequests = new Subscription();

    const requestVars: AdvancedSearchRequestVars = {
      recordCount$: this.totalRecords$,
      recordList$: this.accountList$,
      resultCount: this.resultCount
    };

    const offsets = this.advancedSearchService.getOffsetsForPage(request.payload, requestVars, this.pageSize);

    const requestObs = this.advancedSearchService.getRequestObservables<AccountResponse>(AccountResponse, request, offsets);

    const exactCall = requestObs.exact.subscribe((exactResultObj: AccountResponse) => {
      const parsedExactObj = this.parseResultObj(exactResultObj);
      this.accountList$.next(parsedExactObj.accounts);
      this.isProcessing$.next(LoadingState.partiallyLoaded);
      // only update the count on the first page of results. This prevents total page issues when the object is generated.
      if (offsets.exactOffset === 0) {
        this.resultCount.exact = exactResultObj.count;
      }
      // Update totalRecords$ on new searches or page 1 to ensure pagination resets/updates
      if (this.isNewSearch || offsets.exactOffset === 0) {
        this.totalRecords$.next(parsedExactObj.count);
      }
      const partialCall = requestObs.partial.subscribe((partialResultObj: AccountResponse) => {
        const parsedPartialObj = this.parseResultObj(partialResultObj);
        this.accountList$.next(parsedExactObj.accounts.concat(parsedPartialObj.accounts).slice(0, this.pageSize));
        // only update the count on the first page of results. This prevents total page issues when the object is generated.
        if (offsets.partialOffset === 0) {
          this.resultCount.partial = partialResultObj.count;
        }
        this.totalRecords$.next(this.resultCount.exact + this.resultCount.partial);
        this.isProcessing$.next(LoadingState.fullyLoaded);
      });
      this.pendingRequests.add(partialCall);
    });

    this.pendingRequests.add(exactCall);
  }

  private parseResultObj(resultObj: AccountResponse) {
    resultObj.accounts.forEach(account => {
      account.recordTypeLabel = this.templateValues.RecordType[account.recordType];
      this.setLnStatusLabel(account);
    });
    return resultObj;
  }

  private retrieveAccounts() {
    this.getResultsForRequest(this.accRequest);
  }

  setLnStatusLabel(account) {
    account.lnStatusLabel = account.lnStatus ?
      this.templateValues.LnStatus[account.lnStatus] : this.templateValues.LeadStatus[account.leadStatus];
  }

  /**
   * Maps filters to request
   * @param filterFields - The filter fields
   */
  private mapFilters(filterFields: Partial<AccountsFilters>) {
    this.currentPage$.next(this.accRequest.payload.page);

    this.accRequest.payload.leads = filterFields.leads;
    this.accRequest.payload.myCustomers = filterFields.myCustomers;
    this.accRequest.payload.delivery = filterFields.delivery;
    this.accRequest.payload.accounts = filterFields.accounts;
    this.accRequest.payload.status = filterFields.lnStatus;
    this.accRequest.payload.date = this.getFilterDate(filterFields.filterByInterval);
    this.accRequest.payload.nameFilter = {
      filterType: filterFields.searchBy,
      name: filterFields.searchField,
      employeeId: filterFields.accountManager?.employeeId,
      displayedSearchValue: filterFields.displayedSearchValue,
      partialSearch: false
    };
    if (filterFields.priceMatrix || filterFields.city || filterFields.stateId || filterFields.country || filterFields.postalCode) {
      this.accRequest.payload.advancedFilter = {
        priceMatrix: filterFields.priceMatrix,
        city: filterFields.city,
        stateId: filterFields.stateId,
        countryId: filterFields.country,
        postalCode: filterFields.postalCode
      };
    } else {
      delete this.accRequest.payload.advancedFilter;
    }
  }

  /**
   * Sets the date interval according to filter type
   * @param filterType - By which the date interval gets set
   * @returns - The date interval
   */
  private getFilterDate(filterType: string) {
    const payloadDate: any = {
      startDate: new Date(),
      endDate: new Date()
    };

    const filteredDate = {
      today: () => {
        payloadDate.startDate.setHours(0, 0, 0, 0);
        payloadDate.endDate.setHours(23, 59, 59, 999);
        return payloadDate;
      },
      week: () => {
        payloadDate.startDate.setHours(0, 0, 0, 0);
        payloadDate.endDate.setDate(payloadDate.endDate.getDate() + (6 - payloadDate.endDate.getDay()));
        payloadDate.endDate.setHours(23, 59, 59, 999);
        return payloadDate;
      },
      all: () => {
        payloadDate.startDate = null;
        payloadDate.endDate = null;
        return payloadDate;
      }
    };
    return filteredDate[filterType]();
  }
}
